import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRealtimeWindow, REALTIME_VIEW_SPAN_MS } from "@/map/hooks/useRealtimeWindow";
import type { TimelineWindowMode } from "@/map/components/map-preview/MapTimelineStack";
import type { MapEventRow } from "@/map/hooks/useLocationEvents";

/** Newest AVAILABLE footage clip for the active primary video. */
export interface FootageEdge {
  startMs: number;
  endMs: number;
}

interface UseMapLiveSessionArgs {
  /** Switching the active machine re-arms live-follow and drops stale footage. */
  selectedMachineId: number | null;
  /**
   * Whether an alert is currently selected. Read through a ref inside the re-arm
   * effect so the effect reacts to machine/mode changes ONLY — selecting or
   * clearing an alert manages live-follow itself, and depending on it here would
   * re-arm and roll the view away from the alert being reviewed.
   */
  selectedAlertId: string | null;
  /** Clears the alert review when the operator goes live. */
  onGoLive: () => void;
}

/**
 * The map's realtime/live session: window mode, the rolling window, where the
 * live edge is, and whether the cursor is pinned to it.
 *
 * The central subtlety is that "live" means the newest AVAILABLE FOOTAGE, not
 * wall-clock now — uploads lag real time by minutes, so anything that pins to
 * `Date.now()` runs ahead of the clips that actually exist.
 */
export const useMapLiveSession = ({
  selectedMachineId,
  selectedAlertId,
  onGoLive,
}: UseMapLiveSessionArgs) => {
  // "realtime" swaps the global date range for a rolling live window.
  const [timelineMode, setTimelineMode] = useState<TimelineWindowMode>("realtime");
  // Visible span of the realtime view (user-selectable: 5m / 10m / 30m / 1h).
  const [liveSpanMs, setLiveSpanMs] = useState(REALTIME_VIEW_SPAN_MS);

  // Newest event of the live rolling window, reported by the timeline panel.
  const [liveLatestEvent, setLiveLatestEvent] = useState<MapEventRow | null>(null);
  const [liveFollowPaused, setLiveFollowPaused] = useState(false);
  // Whether the shared cursor + primary video are pinned to the newest moment.
  const [cursorLiveFollow, setCursorLiveFollow] = useState(false);
  // Reported by the primary tile; null until footage loads.
  const [footageEdge, setFootageEdge] = useState<FootageEdge | null>(null);
  // Bumped on each "Go live" so the primary video resets to the live edge.
  const [goLiveTime, setGoLiveTime] = useState<number | null>(null);
  // Timestamp the timeline + video should snap to.
  const [alertSeekTime, setAlertSeekTime] = useState<number | null>(null);

  const selectedAlertIdRef = useRef(selectedAlertId);
  selectedAlertIdRef.current = selectedAlertId;

  useEffect(() => {
    setLiveFollowPaused(false);
    // Entering realtime / switching the active machine re-arms cursor/video
    // live-follow — but NOT when the switch is a side effect of selecting an
    // alert on another machine (then stay on the alert: the video seeks to it and
    // the timeline centers on it, instead of re-arming and rolling away). Range
    // mode never follows.
    setCursorLiveFollow(timelineMode === "realtime" && selectedAlertIdRef.current == null);
    if (selectedAlertIdRef.current == null) setReviewing(false);
    // Drop the previous machine's footage edge so live-follow waits for the new
    // primary's footage instead of using a stale one.
    setFootageEdge(null);
  }, [timelineMode, selectedMachineId]);

  // The live edge (true "now" the cursor tracks) = the END of the newest
  // AVAILABLE footage clip. Falls back to the newest EVENT end only until footage
  // is known (a rough bootstrap so the first fetch lands near the footage) — the
  // event edge runs minutes ahead of footage, so pinning to it is the old bug.
  // Null outside realtime.
  const liveEdge = useMemo(() => {
    if (timelineMode !== "realtime") return null;
    if (footageEdge) return footageEdge.endMs;
    if (!liveLatestEvent) return null;
    return liveLatestEvent.startTime.getTime() + (liveLatestEvent.durationSeconds || 0) * 1000;
  }, [timelineMode, footageEdge, liveLatestEvent]);

  // The newest footage clip's START. Handed to the provider only as the "footage
  // is known / we're live" gate for leave-live detection — the driver never seeks
  // to it (a cursor yank is what broke arming). Null until footage lands.
  const liveFootageStart = useMemo(
    () => (timelineMode === "realtime" && footageEdge ? footageEdge.startMs : null),
    [timelineMode, footageEdge],
  );

  // Whether the operator is actually REVIEWING a chosen moment, as opposed to
  // live-follow merely being off. Set when an alert or an event is selected,
  // cleared by "Go live" and by leaving realtime.
  const [reviewing, setReviewing] = useState(false);

  // Alert-review timeline domain: while reviewing, the stacked timelines freeze
  // to a window CENTERED on that moment (`alertSeekTime`, where the video also
  // seeks) so the alert + cursor stay visible even when older than the live span.
  //
  // Gated on `reviewing`, NOT on `!cursorLiveFollow` alone. Live-follow disarms
  // for reasons that have nothing to do with review — most importantly a video
  // that simply stopped (a stall, the end of available footage, a rollover that
  // raced). With the old gate, any such stop handed the timelines a stale
  // `alertSeekTime` (go-live sets one) and FROZE them at that instant: the video
  // stopping stopped the clock. The rolling window must outlive the video, so
  // only a real review pins it.
  const reviewTime = timelineMode === "realtime" && !cursorLiveFollow && reviewing
    ? alertSeekTime
    : null;

  // Declared AFTER `reviewTime` on purpose: a review pins the fetch window, so
  // the window hook needs to know whether one is in progress. Nothing between
  // here and the states above reads `realtimeWindow`.
  const realtimeWindow = useRealtimeWindow(
    timelineMode === "realtime",
    liveSpanMs,
    reviewTime != null,
  );

  // Stable so the driver's leave-live effects don't re-bind on every page render.
  const handleLeaveLive = useCallback(() => setCursorLiveFollow(false), []);

  // Read through a ref for the same reason: `handleGoLive` is bound once by the
  // visibility listener and must not re-bind as the caller re-renders.
  const onGoLiveRef = useRef(onGoLive);
  onGoLiveRef.current = onGoLive;

  // "Go live": re-arm live-follow, close the alert review, and re-sync the shared
  // cursor + video to the live edge. The cursor seeks to now (drops the review-
  // centered timeline back to the rolling live window); `goLiveTime` tells the
  // primary video to abandon the stale alert-era footage and re-fetch around the
  // live edge so it lands on the newest clip (not crawl forward from the alert).
  const handleGoLive = useCallback(() => {
    const now = Date.now();
    // Also leaves Range mode — alert review switches into it, so going live has
    // to switch back or the rolling window never resumes.
    setTimelineMode("realtime");
    setCursorLiveFollow(true);
    setReviewing(false);
    onGoLiveRef.current();
    setAlertSeekTime(now);
    setGoLiveTime(now);
  }, []);

  /**
   * Arriving at the map — by reload OR by router navigation — lands LIVE, by
   * running the very same flow the "Go live" button runs.
   *
   * Not a partial imitation of it. The mode already defaulted to realtime and
   * follow was armed, so the page LOOKED live while the video started wherever
   * the cursor seed left it and crawled forward; and a hand-rolled subset of
   * go-live would drift from the button the first time either changed. Calling
   * `handleGoLive` means there is one definition of "be live" and both entry
   * points use it: realtime mode, follow armed, review cleared, and the video
   * re-fetched around now so it lands on the newest clip.
   *
   * Runs on MOUNT, which covers both cases — the route unmounts on navigation,
   * so coming back through the router is a fresh mount, not a resumed one.
   *
   * Skipped when the page opened onto an alert (a deep link, or a selection
   * carried in): that asked for a specific moment, not for live.
   */
  useEffect(() => {
    if (selectedAlertIdRef.current != null) return;
    handleGoLive();
    // Mount only. `handleGoLive` is stable, and re-running this on any later
    // change would yank an operator back to live mid-review.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFootageEdgeChange = useCallback(
    (edge: FootageEdge | null) => setFootageEdge(edge),
    [],
  );

  return {
    timelineMode,
    setTimelineMode,
    liveSpanMs,
    setLiveSpanMs,
    realtimeWindow,
    liveLatestEvent,
    setLiveLatestEvent,
    liveFollowPaused,
    setLiveFollowPaused,
    cursorLiveFollow,
    setCursorLiveFollow,
    footageEdge,
    goLiveTime,
    alertSeekTime,
    setAlertSeekTime,
    liveEdge,
    liveFootageStart,
    reviewTime,
    /** Call with `true` when a moment is selected for review, `false` to end it. */
    setReviewing,
    handleGoLive,
    handleLeaveLive,
    handleFootageEdgeChange,
  };
};
